//import { Chart as ChartJS, registerables } from "chart.js";
import { Chart as ChartJS } from "chart.js/auto";
import { useEffect, useRef, useState } from "react";
import MapLayer from "../../models/mapLayer";
import chartService from "../../services/chartService";
import unitConverter from "../../util/units/unitConverter";
import Navigator from "../../models/navigator";
import Navigable, {
  SetFilters,
  SetThematicMapFilters,
} from "../../models/navigable";
import Filter from "../../models/filter";
import { ActiveElement } from "chart.js/dist/plugins/plugin.tooltip";
import ThematicMapLayerFilters from "../../models/thematicMapLayerFilters";
import { SummaryChartDataItem } from "../../models/summaryChartDataItem";
import LoadingIndicatorLargeCenter from "../common/LoadingIndicatorLargeCenter";
import ProjectInfo from "../../models/projectInfo";
import Moment from "react-moment";
import dateTimeHelper from "../../util/dateTimeHelper";

const SummaryChart: React.FC<{
  mapLayer: MapLayer;
  navigator: Navigator;
  project: ProjectInfo;
}> = ({ mapLayer, navigator, project }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [title, setTitle] = useState<string>("");
  const [totalLegth, setTotalLegth] = useState<string>("");
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [currentMeasurementDate, setCurrentMeasurementDate] = useState<Date>();

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const navigable = new Navigable();
    navigable.id = `summaryChart[${mapLayer.layerName}]`;

    setTitle(mapLayer.layerName);

    setCurrentMeasurementDate(project.esaCurrentMeasurementDate);

    setIsBusy(true);

    let chart: ChartJS | undefined;
    let isDisposed = false;

    const init = async (canvas: HTMLCanvasElement) => {
      if (!mapLayer?.thematicMapLayer) {
        return;
      }

      const { data: dataItems } = await chartService.getSummaryChartDataItems(
        mapLayer.thematicMapLayer.idThematicMapLayer,
        navigator.filters,
      );

      if (isDisposed) {
        return;
      }

      const len = dataItems.map((di) => di.numberValue);
      const totalLen = len.reduce((acc, curr) => acc + curr, 0);

      const useArea = project.isEsaSync && mapLayer.thematicMapLayer.useArea;

      if (useArea) {
        let totalAreaSquareYard: any =
          unitConverter.metersSquareToYardSquare(totalLen);
        totalAreaSquareYard = totalAreaSquareYard.toLocaleString();
        setTotalLegth(`${totalAreaSquareYard} (yd^2)`);
      } else {
        let totalLenMiles: any = unitConverter.metersToMiles(totalLen);
        totalLenMiles = totalLenMiles.toLocaleString();
        setTotalLegth(`${totalLenMiles} (mi)`);
      }

      const data = dataItems.map((di) => di.numberValue);
      const labels = dataItems.map((di) => di.thematicMapLayerRange.rangeLabel);
      const backgroundColor = dataItems.map(
        (dv) => dv.thematicMapLayerRange.rangeColor,
      );

      const getExplodeOffset = (chart: ChartJS) => {
        const explodedOffset =
          Math.max(chart.chartArea.width, chart.chartArea.height) / 10;

        return explodedOffset;
      };

      chart?.destroy();

      chart = new ChartJS(canvas, {
        type: "pie",
        data: {
          labels: labels, // x
          datasets: [
            {
              label: "Length",
              data: data, // y
              backgroundColor: backgroundColor,
              borderWidth: 1,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "right",
              // onClick() {},
              labels: {
                generateLabels: (chart) => {
                  const datapoints = chart.data.datasets[0].data as number[];
                  let total = datapoints.reduce((acc, curr) => acc + curr, 0);
                  const datasets = chart.data.datasets;

                  const getFontColor = (i: number) => {
                    const offset = (datasets[0] as any).offset;
                    if (offset) {
                      if (offset[i] !== 0) {
                        return "black";
                      }
                    }
                    return "#666";
                  };

                  const getBoxStorkeColor = (i: number) => {
                    const backgroundColor = datasets[0].backgroundColor as any;

                    if (!backgroundColor) {
                      return undefined;
                    }

                    const offset = (datasets[0] as any).offset;
                    if (offset) {
                      if (offset[i] !== 0) {
                        return undefined;
                      }
                    }

                    return backgroundColor[i];
                  };

                  return datasets[0].data.map((data, i) => ({
                    text: chart.data.labels
                      ? `${chart.data.labels[i]} ${
                          Math.round((Number(data) / total) * 100) + "%"
                        }`
                      : "",
                    fontColor: getFontColor(i),
                    fillStyle: datasets[0].backgroundColor
                      ? (datasets[0].backgroundColor as any)[i]
                      : undefined,
                    strokeStyle: getBoxStorkeColor(i),
                    lineWidth: 3,
                    hidden: !chart.getDataVisibility(i),
                    index: i,
                  }));
                },
                font: {
                  size: 13,
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  //let label = context.label || "";
                  let value: any = context.raw || 0;
                  const datapoints = context.chart.data.datasets[0]
                    .data as number[];
                  let total = datapoints.reduce((acc, curr) => acc + curr, 0);
                  let percentage = Math.round((value / total) * 100) + "%";

                  const useArea =
                    project.isEsaSync && mapLayer.thematicMapLayer?.useArea;

                  var unit = "";

                  if (useArea) {
                    unit = "(yd^2)";
                    value = unitConverter.metersSquareToYardSquare(value);
                    value = value.toLocaleString();
                  } else {
                    unit = "(mi)";
                    value = unitConverter.metersToMiles(value);
                    value = value.toLocaleString();
                  }

                  return " " + value + " " + unit + " " + percentage;
                },
              },
            },
          },
          onClick: (event: any, activeElements: ActiveElement[], chart) => {
            // console.log("chartOnClick");

            const getSelectedIndex = (dataset: any) => {
              const offset = dataset.offset as number[] | undefined;

              if (!offset) {
                return -1;
              }

              var index = offset.findIndex((el) => el !== 0);

              return index ?? -1;
            };

            const explodedOffset = getExplodeOffset(chart);

            const dataset: any = chart.data.datasets[0];

            const prevSelectedIndex = getSelectedIndex(dataset);

            if (activeElements.length) {
              const activeIndex = activeElements[0].index;
              dataset.offset = dataset.data.map((_: any, idx: number) => {
                if (idx === activeIndex) {
                  if (!dataset.offset) {
                    return explodedOffset;
                  } else {
                    if (dataset.offset[activeIndex] === 0) {
                      return explodedOffset;
                    }
                  }
                }
                return 0;
              });
            } else {
              // click outside resets all
              dataset.offset = dataset.data.map(() => 0);
            }

            chart.update();

            const selectedIndex = getSelectedIndex(dataset);

            if (selectedIndex !== -1) {
              const selectedChartItem = dataItems[selectedIndex];
              // console.log("selected chartItem", selectedChartItem);
              onSelectItem(selectedChartItem);
            } else {
              if (prevSelectedIndex !== -1) {
                //console.log("deselected chartItem");
                onDeselectItem();
              }
            }
          },
          onResize: (chart) => {
            if (!chart.chartArea) {
              return;
            }

            const explodedOffset = getExplodeOffset(chart);

            const dataset: any = chart.data.datasets[0];
            dataset.offset = dataset.data.map((_: any, idx: number) => {
              if (dataset.offset) {
                if (dataset.offset[idx] !== 0) {
                  return explodedOffset;
                }
              }

              return 0;
            });

            chart.update("none");
          },
        },
      });

      setIsBusy(false);
    };

    init(canvasRef.current);

    const onSelectItem = (chartDataItem: SummaryChartDataItem) => {
      const thematicMapLayerRange =
        mapLayer.thematicMapLayer?.thematicMapLayerRanges.find(
          (r) =>
            r.idThematicMapLayerRange ===
            chartDataItem.thematicMapLayerRange.idThematicMapLayerRange,
        );
      if (thematicMapLayerRange) {
        navigator.setThematicMapFilters(
          {
            mapLayer: mapLayer,
            thematicMapLayerRange: thematicMapLayerRange,
          },
          navigable,
        );
      }
    };

    const onDeselectItem = () => {
      navigator.setThematicMapFilters(undefined);
    };

    const clearSelectedItem = () => {
      if (!chart) {
        return;
      }

      const dataset: any = chart.data.datasets[0];
      dataset.offset = dataset.data.map(() => 0);

      chart.update();
    };

    const setFilters: SetFilters = async (
      filters: Filter[],
      isProjectChanging: boolean,
    ) => {
      if (isDisposed) {
        return;
      }

      if (!canvasRef.current) {
        return;
      }

      setIsBusy(true);
      await init(canvasRef.current);
      setIsBusy(false);
    };
    navigable.setFilters = setFilters;

    const setThematicMapFilters: SetThematicMapFilters = (
      thematicMapLayerFilters: ThematicMapLayerFilters | undefined,
    ) => {
      //console.log("summaryChart-setThematicMapFilters-received");
      clearSelectedItem();
    };
    navigable.setThematicMapFilters = setThematicMapFilters;

    navigator.attach(navigable);

    return () => {
      navigator.detach(navigable);
      isDisposed = true;
      chart?.destroy();
    };
  }, [mapLayer, navigator, project]);

  return (
    <div
      className="camera-img-container d-flex flex-column align-items-center"
      style={{
        paddingTop: "30px",
        paddingRight: "5px",
        paddingBottom: "5px",
        paddingLeft: "5px",
      }}
    >
      <div style={{ position: "absolute", top: "4px", fontWeight: "bold" }}>
        {`${title} Summary ${totalLegth}`}
      </div>
      {currentMeasurementDate && (
        <div
          style={{
            position: "absolute",
            bottom: "0px",
            fontSize: "10px",
            right: "10px",
            fontWeight: "bold",
          }}
        >
          {`Current ${title} Date:`}{" "}
          <Moment format={dateTimeHelper.dateFormat}>
            {currentMeasurementDate}
          </Moment>
        </div>
      )}
      {isBusy && <LoadingIndicatorLargeCenter />}
      <canvas ref={canvasRef} />
    </div>
  );
};

export default SummaryChart;
